import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Search, StickyNote, Archive, ArchiveRestore } from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { NoteCard } from '../components/NoteCard';
import { NoteEditor } from '../components/NoteEditor';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const Notas = () => {
  const [notes, setNotes] = useState([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [q, setQ] = useState('');
  const [view, setView] = useState('ativas');
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/notes', { params: { q, archived: view === 'arquivadas' ? '1' : '0' } });
      setNotes(data.notes);
      setArchivedCount(data.archived_count);
    } catch (e) { toast({ title: 'Erro ao carregar notas', description: e.response?.data?.detail || '' }); }
  }, [q, view]);

  useEffect(() => { load(); }, [load]);

  const newNote = () => { setEditing(null); setOpen(true); };
  const openNote = (n) => { setEditing(n); setOpen(true); };
  const pin = async (n) => { await api.put(`/notes/${n.id}`, { pinned: !n.pinned }); load(); };
  const archive = async (n) => {
    await api.put(`/notes/${n.id}`, { archived: !n.archived });
    toast({ title: n.archived ? 'Nota restaurada' : 'Nota arquivada' });
    load();
  };
  const confirmDelete = async () => {
    await api.delete(`/notes/${toDelete.id}`);
    toast({ title: 'Nota excluída' });
    setToDelete(null);
    load();
  };

  return (
    <div className="space-y-4" data-testid="notes-module">
      <div className="ent-panel p-3 flex items-center gap-2.5 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="notes-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nas minhas notas..."
            className="ent-input w-full" style={{ paddingLeft: 30 }} />
        </div>
        <div className="ent-tabs">
          <button data-testid="notes-tab-ativas" onClick={() => setView('ativas')} className={`ent-tab ${view === 'ativas' ? 'ent-tab-active' : ''}`}>
            <StickyNote className="w-3 h-3" /> Minhas notas
          </button>
          <button data-testid="notes-tab-arquivadas" onClick={() => setView('arquivadas')} className={`ent-tab ${view === 'arquivadas' ? 'ent-tab-active' : ''}`}>
            <Archive className="w-3 h-3" /> Arquivadas {archivedCount ? `(${archivedCount})` : ''}
          </button>
        </div>
        <button data-testid="notes-new-btn" onClick={newNote} className="ent-btn ent-btn-primary">
          <Plus className="w-3.5 h-3.5" /> Nova nota
        </button>
      </div>

      {notes.length === 0 ? (
        <div className="ent-panel p-10 text-center space-y-2" data-testid="notes-empty">
          {view === 'arquivadas' ? <ArchiveRestore className="w-6 h-6 mx-auto text-muted-foreground" /> : <StickyNote className="w-6 h-6 mx-auto text-muted-foreground" />}
          <p className="text-[13px] text-muted-foreground">
            {q ? 'Nenhuma nota encontrada para essa busca.' : view === 'arquivadas' ? 'Nenhuma nota arquivada.' : 'Suas notas são privadas — só você as vê. Crie a primeira.'}
          </p>
          {view === 'ativas' && !q && <button data-testid="notes-empty-new" onClick={newNote} className="ent-btn ent-btn-primary mx-auto"><Plus className="w-3.5 h-3.5" /> Nova nota</button>}
        </div>
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-3" data-testid="notes-grid">
          {notes.map((n) => (
            <NoteCard key={n.id} note={n} onOpen={openNote} onPin={pin} onArchive={archive} onDelete={setToDelete} />
          ))}
        </div>
      )}

      <NoteEditor note={editing} open={open} onClose={() => setOpen(false)} onSaved={load} />

      <Dialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md">
          <DialogHeader><DialogTitle className="ent-coltitle uppercase tracking-wide">Excluir nota</DialogTitle></DialogHeader>
          <p className="text-[13px] text-foreground/80">
            A nota <b>{toDelete?.title || 'sem título'}</b> e seus anexos serão apagados. Esta ação não pode ser desfeita.
          </p>
          <DialogFooter>
            <button data-testid="notes-delete-cancel" onClick={() => setToDelete(null)} className="ent-btn ent-btn-sm">Cancelar</button>
            <button data-testid="notes-delete-confirm" onClick={confirmDelete} className="ent-btn ent-btn-sm text-[hsl(var(--bad))] border-[hsl(var(--bad))]/50">Excluir</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Notas;
